import React, { ChangeEvent, useEffect, useState } from "react";
import clsx from "clsx";

import InputLabel from "@material-ui/core/InputLabel";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";

import Loader from "components/Loader";
import { ISelectOption } from "interfaces/ISelectOption";
import FieldErrorMessage from "../FormValidation/FieldErrorMessage";
import { CUSTOM_VALUE } from "../VetExamOverlay/helper";
import { EVetExamFields } from "../VetExamOverlay/options";
import useStyles from "./styles";

interface IProps {
  options: ISelectOption[];
  label: string;
  inputLabel?: string;
  selectedValue: number | string;
  onChangeSelectedValue: (value: number | string, customValue?: string) => void;
  name?: EVetExamFields;
  className?: string;
  hasCustomField?: boolean;
  dataTestLabel?: string;
  showInnerLoader?: boolean;
  errorMessage?: string;
  shouldSort?: boolean;
  disabled?: boolean;
}

const otherField = {
  name: "Other...",
  value: "other"
};

const CustomSelect = (props: IProps) => {
  const {
    options,
    label = "",
    name,
    inputLabel = "",
    selectedValue,
    onChangeSelectedValue,
    className = "",
    errorMessage = "",
    hasCustomField = false,
    showInnerLoader = false,
    dataTestLabel,
    shouldSort = false,
    disabled = false
  } = props;
  const classes = useStyles();

  const [isShowLoader, setIsShowLoader] = useState<boolean>(false);
  const [isOpenSelect, setIsOpenSelect] = useState<boolean>(true);
  const [customValue, setCustomValue] = useState<string>("");
  const [shouldShowOtherPopup, setShouldShowOtherPopup] =
    useState<boolean>(false);

  const isLoading = [showInnerLoader, isShowLoader].every(Boolean);

  const handleChangeValue = (
    event: ChangeEvent<{ name?: EVetExamFields; value: string | number }>
  ) => {
    showInnerLoader && setIsShowLoader(true);
    const { value } = event.target;

    if (value === otherField.value) {
      setShouldShowOtherPopup(true);
      return;
    }

    setCustomValue("");
    onChangeSelectedValue(value);
  };

  const handleChangeCustomField = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setCustomValue(event.target.value);
  };

  const handleCloseOtherPopup = () => {
    setShouldShowOtherPopup(false);
  };

  const handleSaveCustomValue = async () => {
    onChangeSelectedValue(CUSTOM_VALUE, customValue);
    setShouldShowOtherPopup(false);
  };

  const onFocusSelect = () => {
    setIsOpenSelect(true);
  };

  const onBlurSelect = () => {
    setIsOpenSelect(false);
  };

  const renderOptions = () => {
    const preparedOptions = [...options];

    if (shouldSort) {
      preparedOptions.sort((item1, item2) =>
        item1.name.localeCompare(item2.name)
      );
    }

    if (hasCustomField) {
      preparedOptions.push(otherField);
    }

    return preparedOptions.map((item: ISelectOption) => {
      let className = "";

      if (item.value === CUSTOM_VALUE) {
        className = classes.customField;
      }

      return (
        <MenuItem
          key={`${item.value}_${item.name}`}
          className={className}
          value={item.value}
        >
          {item.name}
        </MenuItem>
      );
    });
  };

  useEffect(() => {
    const customOption = options.find(item => item.value === CUSTOM_VALUE);

    setCustomValue(customOption?.name || "");
    showInnerLoader && setIsShowLoader(false);
  }, [selectedValue]);

  return (
    <>
      <FormControl
        className={className}
        classes={{
          root: clsx(classes.rootFormControl, {
            selectedValue,
            hasError: Boolean(errorMessage)
          })
        }}
        variant="outlined"
        data-test-id={dataTestLabel}
      >
        {isLoading && <Loader size={19} className={classes.selectLoader} />}
        <InputLabel id="exam-select">
          {isOpenSelect || selectedValue ? label : inputLabel}
        </InputLabel>
        <Select
          classes={{
            root: classes.selectRoot
          }}
          labelId="exam-select"
          id="exam-select-filled"
          value={selectedValue}
          MenuProps={{
            classes: {
              list: classes.selectList,
              paper: classes.selectPaper
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left"
            },
            getContentAnchorEl: null
          }}
          label={label}
          defaultValue={""}
          onChange={handleChangeValue}
          onFocus={onFocusSelect}
          onBlur={onBlurSelect}
          name={name}
          disabled={disabled}
        >
          {renderOptions()}
        </Select>
        <FieldErrorMessage className={classes.errorMessage}>
          {errorMessage}
        </FieldErrorMessage>
      </FormControl>

      {shouldShowOtherPopup && (
        <Dialog open={true} onClose={handleCloseOtherPopup}>
          <DialogContent className={classes.otherPopup}>
            <div className={classes.otherPopupHeader}>{label}</div>
            <TextField
              className={classes.popupTextField}
              label={null}
              variant="outlined"
              value={customValue}
              onChange={handleChangeCustomField}
              autoFocus={true}
              inputProps={{
                maxLength: 200
              }}
            />
            <div className={classes.popupActions}>
              <Button
                className={clsx(classes.popupButton)}
                onClick={handleCloseOtherPopup}
              >
                Cancel
              </Button>
              <Button
                className={clsx(classes.popupButton, classes.saveButton)}
                onClick={handleSaveCustomValue}
                disabled={!customValue.trim()}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default React.memo(CustomSelect);
